import {
  Campaign,
  CampaignStatusesEnum,
  CampaignStatusMessages,
} from '@/client/campaigns';
import { Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useCampaign } from '@/hooks/query';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  getCampaignRangeDate,
  getClickRate,
  getReportRate,
  getSubmissionRate,
} from '@/utils/helpers';
import { Skeleton } from 'primereact/skeleton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledSkeleton = styled(Skeleton)`
  background-color: var(--gray-opacity-light);
`;

const ActiveBanner = styled.div`
  width: 100%;
  background-color: var(--green-chart-dark);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--default-padding);
  margin-top: var(--default-padding);
  cursor: pointer;

  .banner-info {
    display: flex;
    flex-direction: column;
    gap: var(--xsmall-padding);
    min-width: 0;
  }

  .banner-top-row {
    display: flex;
    align-items: center;
    gap: var(--medium-padding);
    flex-wrap: wrap;
  }

  .status-badge {
    width: fit-content;
    background-color: var(--green-pale);
    border-radius: var(--grand-border-radius);
    padding: var(--xxsmall-padding) var(--small-padding);
    font-size: var(--xsmall-font-size);
    line-height: 150%;
    font-weight: 500;
    color: var(--green-chart-dark);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--green-main);
  }

  .see-live-results {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 500;
    color: var(--white-main);
    cursor: pointer;
    white-space: nowrap;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .live-dot {
    width: 7px;
    min-width: 7px;
    max-width: 7px;
    height: 7px;
    min-height: 7px;
    max-height: 7px;
    border-radius: 50%;
    background-color: var(--green-main);
    display: inline-block;
    vertical-align: middle;
    transform: scale(1);
    box-shadow: 0 0 0 var(--green-main);
    animation: anim-vibrate 1.5s infinite;

    @keyframes anim-vibrate {
      0% {
        transform: scale(0.85);
        box-shadow: 0 0 0 0 var(--green-main);
      }

      60% {
        transform: scale(1);
        box-shadow: 0 0 0 0.2rem transparent;
      }

      100% {
        transform: scale(0.85);
        box-shadow: 0 0 0 0 transparent;
      }
    }
  }

  .campaign-name {
    font-size: var(--heading-small-font-size);
    line-height: var(--heading-small-line-height);
    font-weight: 700;
    color: var(--white-main);
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stats-container {
    display: flex;
    align-items: center;
    gap: 0;
    flex-shrink: 0;
  }

  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 var(--default-padding);
    border-left: 1px solid rgba(255, 255, 255, 0.2);

    &:last-child {
      padding-right: 0;
    }
  }

  .stat-label {
    font-size: var(--xsmall-font-size);
    line-height: var(--xsmall-line-height);
    font-weight: 400;
    color: rgba(255, 255, 255, 0.7);
    white-space: nowrap;
  }

  .stat-value {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
    font-weight: 700;
    color: var(--white-main);
  }

  @media (max-width: 899px) {
    flex-direction: column;
    align-items: flex-start;

    .stats-container {
      width: 100%;
      justify-content: flex-start;
    }

    .stat-item:first-child {
      padding-left: 0;
      border-left: none;
    }
  }
`;

export const ActiveCampaignBanner: React.FC<{
  initialCampaign: Campaign;
}> = ({ initialCampaign }) => {
  const { t } = useTranslation();
  const { canUseFeature } = useFeatureFlag();
  const navigate = useNavigate();

  const { isLoading, campaign } = useCampaign(initialCampaign.id, {
    keepPreviousData: true,
  });

  if (isLoading || !campaign) {
    return (
      <ActiveBanner>
        <div className="banner-info">
          <StyledSkeleton width="180px" height="24px" borderRadius="100px" />
          <StyledSkeleton width="320px" height="24px" borderRadius="4px" />
        </div>
        <div className="stats-container">
          {[0, 1, 2].map((i) => (
            <div className="stat-item" key={i}>
              <StyledSkeleton width="80px" height="14px" borderRadius="4px" />
              <StyledSkeleton
                width="50px"
                height="28px"
                borderRadius="4px"
                className="mt-1"
              />
            </div>
          ))}
        </div>
      </ActiveBanner>
    );
  }

  return (
    <ActiveBanner
      onClick={() =>
        navigate(RedirectPaths[RedirectPathsEnum.CAMPAIGNS_REPORT](campaign.id))
      }
    >
      <div className="banner-info">
        <div className="banner-top-row">
          <span className="status-badge">
            {t(CampaignStatusMessages[CampaignStatusesEnum.ACTIVE])}
            {' \u00b7 '}
            {getCampaignRangeDate(campaign)}
          </span>
          <span className="see-live-results">
            <span className="live-dot" />
            {t('dashboard.banner.seeLiveResults')}
          </span>
        </div>
        <h3 className="campaign-name">{campaign.name}</h3>
      </div>
      <div className="stats-container">
        <div className="stat-item">
          <span className="stat-label">{t('phishing.chart.clickRate')}</span>
          <span className="stat-value">{getClickRate(campaign)}%</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">
            {t('phishing.chart.submissionRate')}
          </span>
          <span className="stat-value">{getSubmissionRate(campaign)}%</span>
        </div>
        {canUseFeature(Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING) && (
          <div className="stat-item">
            <span className="stat-label">{t('phishing.chart.reportRate')}</span>
            <span className="stat-value">{getReportRate(campaign)}%</span>
          </div>
        )}
      </div>
    </ActiveBanner>
  );
};
